/**
 * ShowMoreSection - Collapsible wrapper for unchanged entity content.
 *
 * For UPDATED entities, unchanged fields and sections are hidden
 * behind a "Show more" toggle. For CREATED/DELETED, children render
 * directly without the toggle.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import type { EntityChangeType } from '@/services/api/generated/entityChangesService';

interface ShowMoreSectionProps {
  changeType: EntityChangeType;
  children: React.ReactNode;
}

export const ShowMoreSection: React.FC<ShowMoreSectionProps> = ({
  changeType,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);

  // For CREATED/DELETED, always show everything
  if (changeType === 'created' || changeType === 'deleted') {
    // eslint-disable-next-line react/jsx-no-useless-fragment -- Single child wrapper needed for conditional return
    return <>{children}</>;
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors py-1"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span>{expanded ? 'Show less' : 'Show more'}</span>
      </button>
      {expanded && <div className="space-y-2 mt-1">{children}</div>}
    </div>
  );
};
